import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { GET_RECIPIENT_PARTY } from '../../graphql/recipients/queries';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/table';
import { Button } from '../../components/button';
import { Badge } from '../../components/badge';
import { PlusIcon, PencilIcon, EyeIcon, ArrowLeftIcon, DocumentTextIcon, EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import { Input, InputGroup } from '../../components/input';
import { MagnifyingGlassIcon } from '@heroicons/react/16/solid';
import { Divider } from '../../components/divider';
import { Dropdown, DropdownButton, DropdownDivider, DropdownItem, DropdownLabel, DropdownMenu } from '../../components/dropdown';
import { Pagination, PaginationList, PaginationNext, PaginationPage, PaginationPrevious } from '../../components/pagination';
import { getPageRange } from '../../utils/paginationHelper';
import loadingImage from '../../assets/images/boat-loading.gif';

const RecipientParties = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchString, setSearchString] = useState('');
  const [isSearch, setIsSearch] = useState(false);
  const [recipientList, setRecipientList] = useState(null);
  const [recipientCount, setRecipientCount] = useState(0);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const totalPages = Math.ceil(recipientCount / rowsPerPage);

  const [getRecipients, { loading, data, error }] = useLazyQuery(GET_RECIPIENT_PARTY, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      console.log('Recipients data received:', data);
      if (data.getRecipientParty.success && data.getRecipientParty.code === '200') {
        setRecipientList(data.getRecipientParty.recipients);
        setRecipientCount(data.getRecipientParty.total_count);
      }
    },
  });

  useEffect(() => {
    fetchRecipients();
  }, [page, rowsPerPage, searchString]);

  const fetchRecipients = (searchValue = searchString) => {
    console.log('Fetching recipients with:', { searchValue, page, rowsPerPage });
    getRecipients({
      variables: {
        searchFilter: {
          active: null,
          recipient_id: null,
          search_string: searchValue || null
        },
        page: { 
          limit: rowsPerPage, 
          offset: page * rowsPerPage 
        }
      }
    });
  };

  const onClickSearch = () => {
    console.log('searchString', searchString);
    setPage(0); // Reset to first page when searching
    fetchRecipients(searchString);
  };

  const onClickSearchClear = () => {
    console.log('searchString before clear:', searchString);
    setSearchString('');
    setPage(0); // Reset to first page when clearing
    fetchRecipients('');
  };

  const handleAddNew = () => {
    navigate('/system-config/recipient-parties/add');
  };

  const handleEdit = (recipient) => {
    navigate(`/system-config/recipient-parties/edit/${recipient.recipient_id}`);
  };

  const handleView = (recipient) => {
    navigate(`/system-config/recipient-parties/details/${recipient.recipient_id}`);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const getCountryName = (countryCode) => {
    const countries = {
      'BH': 'Bahrain',
      'AE': 'UAE',
      'SA': 'Saudi Arabia',
      'QA': 'Qatar',
      'KW': 'Kuwait',
      'OM': 'Oman'
    };
    return countries[countryCode] || countryCode;
  };

  return (
    <>
      <div className="mb-6 flex justify-between items-center">
        <button
          onClick={() => {
            navigate('/')
          }}
          type="button"
          className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
        >
          <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
          {t('Home')}
        </button>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-sm:w-full sm:flex-1">
          <div>
            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap justify-between">
              <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                    <DocumentTextIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                  </div>
                  <div>
                    <span className="ms-3"> {t('Manage Recipient Parties')}</span>
                    <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                      {t('Easily view, add, and edit recipient parties for email notifications.')}
                    </p>
                  </div>
                </h1>
              </div>
              <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap sm:text-end">
                <button
                  onClick={() => navigate('/system-config/recipient-parties/add')}
                  type="button"
                  className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                  {t('Add Recipient')}
                </button>
              </div>
            </div>
          </div>
          <div className="mt-8 mb-4 flex flex-col sm:flex-row flex-wrap max-w-xl gap-4">
            <div className="flex-2">
              <InputGroup>
                <MagnifyingGlassIcon />
                <Input
                  value={searchString}
                  onChange={e => setSearchString(e.target.value)}
                  name="search"
                  id="search"
                  placeholder={t('Search recipients')}
                />
              </InputGroup>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={onClickSearch}
                type="button"
                className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
              >
                {t('Search')}
              </button>
              <button
                onClick={onClickSearchClear}
                type="button"
                className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                {t('Clear')}
              </button>
            </div>
          </div>
        </div>
      </div>
      <Divider />

      {loading && (
        <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
          <img alt="logo" src={loadingImage} className="w-[250px]" />
          <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
        </div>
      )}
      {error && (
        <div className="flex justify-center items-center bg-white py-5">
          <div className="text-lg text-center">{t('Failed to fetch recipients!')}</div>
        </div>
      )}

      {!loading && !error && recipientList?.length === 0 && (
        <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
          <svg
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
            className="mx-auto h-12 w-12 text-gray-400"
          >
            <path
              d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
              strokeWidth={2}
              vectorEffect="non-scaling-stroke"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No data found!')}</h3>
          <p className="mt-1 text-sm text-gray-500">{t('Try adjusting your search criteria')}</p>
          <div className="mt-6"></div>
        </div>
      )}
      {!loading && !error && recipientList?.length > 0 && (
        <>
          <div className="table-custom rounded-b-lg bg-white p-0 flex">
            <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)] w-full">
              <TableHead>
                <TableRow>
                  <TableHeader>{t('Name')}</TableHeader>
                  <TableHeader>{t('Email')}</TableHeader>
                  <TableHeader>{t('Organization')}</TableHeader>
                  <TableHeader>{t('Country')}</TableHeader>
                  <TableHeader>{t('Status')}</TableHeader>
                  <TableHeader>{t('Actions')}</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {recipientList.map(recipient => (
                  <TableRow
                    key={recipient.recipient_id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => navigate(`/system-config/recipient-parties/details/${recipient.recipient_id}`)}
                    title={`Recipient #${recipient.recipient_id}`}
                  >
                    <TableCell className="min-w-[120px]">{recipient.recipient_info.name}</TableCell>
                    <TableCell className="min-w-[200px]">{recipient.recipient_info.email}</TableCell>
                    <TableCell className="min-w-[150px]">{recipient.recipient_info.organization}</TableCell>
                    <TableCell className="min-w-[80px]">{getCountryName(recipient.country_code)}</TableCell>
                    <TableCell>
                      <Badge color={recipient.active ? 'emerald' : 'rose'}>
                        {recipient.active ? t('Active') : t('Inactive')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Dropdown>
                        <DropdownButton
                          plain
                          aria-label="More options"
                          onClick={e => e.stopPropagation()}
                          className="cursor-pointer"
                        >
                          <EllipsisVerticalIcon />
                        </DropdownButton>
                        <DropdownMenu anchor="bottom">
                          <DropdownItem
                            onClick={e => {
                              e.stopPropagation()
                              navigate(`/system-config/recipient-parties/details/${recipient.recipient_id}`)
                            }}
                          >
                            <EyeIcon />
                            <DropdownLabel>{t('View')}</DropdownLabel>
                          </DropdownItem>
                          <DropdownDivider />
                          <DropdownItem
                            onClick={e => {
                              e.stopPropagation()
                              navigate(`/system-config/recipient-parties/edit/${recipient.recipient_id}`)
                            }}
                          >
                            <PencilIcon />
                            <DropdownLabel>{t('Edit')}</DropdownLabel>
                          </DropdownItem>
                        </DropdownMenu>
                      </Dropdown>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Divider />
          {totalPages > 1 && (
            <Pagination className="py-6">
              <PaginationPrevious
                onClick={() => handlePageChange(page > 0 ? page - 1 : 0)}
                isDisabled={page === 0}
              />
              <PaginationList>
                {getPageRange(page, totalPages).map(index => (
                  <PaginationPage
                    key={index}
                    current={index === page}
                    onClick={() => handlePageChange(index)}
                  >
                    {index + 1}
                  </PaginationPage>
                ))}
              </PaginationList>
              <PaginationNext
                onClick={() => handlePageChange(page < totalPages - 1 ? page + 1 : totalPages - 1)}
                isDisabled={page === totalPages - 1}
              />
            </Pagination>
          )}
          <Divider />
        </>
      )}
    </>
  );
};

export default RecipientParties;
